import React, {useEffect, useState} from 'react';
import {Button, Form, message, Modal, Select} from 'antd';
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType,
    GET_ALL_EXCEPTION
} from '../pages/delivery/parcels/graphql/queries/getAllException';
import {useQuery} from '@apollo/client';
import ImageUploader from "./ImageUploader";
import {OrderType} from "../graphql/types/OrderType";
import {filterExceptionByUserRole} from "../config/roleExceptionConfig";
import { SiteType } from 'graphql/types/SiteType';
import { hasHeadquartersAccess, isFinancialTeam, isKAQC, isCSFollowUp, isCS } from 'utils/auth';
import { t } from 'i18n';
import { useStatusInternationalization } from '../pages/delivery/parcels/hooks/useStatusInternationalization';
import { useWarehouseContext } from '../contexts/WarehouseContext';
import { useGetAllAccessHubAndWarehousesForRole } from '../hooks/useWarehouseQuery';

/** 需必选二级原因的异常码：
 * ERDL001/ERDSP004 地址异常
 * ERDL002/ERDSP005 当日无法进入
 * ERDL006/ERDSP008 地址无法投递
 */
const ADDRESS_EXCEPTION_CODES = ['ERDL001', 'ERDSP004', 'ERDL002', 'ERDSP005', 'ERDL006', 'ERDSP008'];
const MULTI_SELECT_SUB_REASON_CODES = ['ERDL001', 'ERDSP004'];

// 显示模式枚举
enum DisplayMode {
  SHOW_HUB_WAREHOUSE = 'show-hub-warehouse',
  HIDE_HUB_WAREHOUSE = 'hide-hub-warehouse'
}

interface MarkAsExceptionModalProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: (values: MarkAsExceptionData) => void;
    loading?: boolean;
    parcel: OrderType;
    initialData?: MarkAsExceptionData;
    userRole: string;
}

export interface MarkAsExceptionData {
    exceptionReasonCode: string | null;
    exceptionHubId: string | null;
    exceptionWarehouseId: string | null;
    photos: string[];
    exceptionSubReasons?: string[] | null;
}

const MarkAsExceptionModal: React.FC<MarkAsExceptionModalProps> = ({
                                                                   visible,
                                                                   onClose,
                                                                   onConfirm,
                                                                   loading = false,
                                                                   parcel,
                                                                   initialData,
                                                                   userRole
                                                               }) => {
    const [form] = Form.useForm<MarkAsExceptionData & { exceptionSubReasonCodes?: string[] }>();
    const [exceptionSelectData, setExceptionSelectData] = useState<ExceptionDictionaryType[]>([]);
    const [showImageUploader, setShowImageUploader] = useState<boolean>(true);
    const [warehouseListForHubData, setWarehouseListForHubData] = useState<any[]>([]);

    const {data, loading: queryLoading} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION,{
        skip:!visible
    });
    const isHeadquartersStaffRole = hasHeadquartersAccess();
    const isKAQCRole = isKAQC();
    const isCSRole = isCS();
    const isFinancialTeamRole = isFinancialTeam();
    const isCSFollowUpRole = isCSFollowUp();
    const exceptionList = data?.getExceptionDictionaryForHeadquarters || [];

    // 获取当前仓库信息
    const { currentWarehouse } = useWarehouseContext();

    // 判断显示模式
    const getDisplayMode = (): DisplayMode => {
        if ((isHeadquartersStaffRole || isKAQCRole || isCSRole || isFinancialTeamRole || isCSFollowUpRole) && !currentWarehouse) {
            return DisplayMode.SHOW_HUB_WAREHOUSE;
        }

      return DisplayMode.HIDE_HUB_WAREHOUSE;
    };

    const displayMode = getDisplayMode();

    // 数据查询 - 统一使用 useGetAllAccessHubAndWarehousesForRole
    const {
      warehouses: warehouseData,
      loading: warehousesLoading,
      error: warehousesError
    } = useGetAllAccessHubAndWarehousesForRole({
      skip: displayMode === DisplayMode.HIDE_HUB_WAREHOUSE
    });

    // 使用状态国际化hook
    const { getExceptionReasonText, getExceptionSubReasonTextByCode } = useStatusInternationalization();

    const selectedReasonCode = Form.useWatch('exceptionReasonCode', form);
    const selectedException = exceptionSelectData.find(e => e.exceptionReasonCode === selectedReasonCode);
    const isAddressException = selectedReasonCode != null && ADDRESS_EXCEPTION_CODES.includes(selectedReasonCode);
    const addressSubReasons = selectedException?.exceptionSubReasons ?? [];
    const isMultiSelectSubReason = selectedReasonCode != null && MULTI_SELECT_SUB_REASON_CODES.includes(selectedReasonCode);

    useEffect(() => {
        if (!exceptionList.length) return;
        const exceptionData = filterExceptionByUserRole(exceptionList, userRole, currentWarehouse?.siteType);
        setExceptionSelectData(exceptionData);
    }, [exceptionList, userRole, currentWarehouse?.siteType]);

    useEffect(() => {
        if (visible && parcel) {
            form.resetFields();

            const defaultValues = {
                exceptionReasonCode: null,
                exceptionHubId: null,
                exceptionWarehouseId: null,
                photos: [],
                exceptionSubReasonCodes: [] as string[]
            };
            
            form.setFieldsValue(defaultValues);
        }
    }, [visible]);

    const handleSubmit = () => {
        if (!form.getFieldValue('exceptionReasonCode')){
            message.warning(t('components.modal.selectExceptionReason'));
            return;
        }

        const photoObjectKeys = form.getFieldValue('photos') || [];
        const selectedExceptionCode = form.getFieldValue('exceptionReasonCode');
        const selectedException = exceptionSelectData.find(
            exception => exception.exceptionReasonCode === selectedExceptionCode
        );

        if (selectedException?.isUploadsImages && photoObjectKeys.length < 1) {
            message.warning(t('components.modal.uploadAtLeastOnePhoto'));
            return;
        }

        const isAddressException = ADDRESS_EXCEPTION_CODES.includes(selectedExceptionCode);
        if (isAddressException) {
            const rawSubCodes = form.getFieldValue('exceptionSubReasonCodes');
            const subCodes = Array.isArray(rawSubCodes) ? rawSubCodes : rawSubCodes ? [rawSubCodes] : [];
            if (!subCodes.length) {
                message.warning(t('components.modal.pleaseSelectExceptionSubReason'));
                return;
            }
        }

        form.validateFields()
            .then((values: MarkAsExceptionData & { exceptionSubReasonCodes?: string[] | string }) => {
                const rawSubCodes = values.exceptionSubReasonCodes;
                const normalizedSubCodes = Array.isArray(rawSubCodes) ? rawSubCodes : rawSubCodes ? [rawSubCodes] : [];
                const isAddrEx = ADDRESS_EXCEPTION_CODES.includes(values.exceptionReasonCode || '');
                const exceptionSubReasons = isAddrEx && normalizedSubCodes.length ? normalizedSubCodes : undefined;

                const submitData: MarkAsExceptionData = {
                    ...values,
                    exceptionReasonCode: values.exceptionReasonCode,
                    exceptionHubId: values.exceptionHubId,
                    exceptionWarehouseId: values.exceptionWarehouseId,
                    photos: photoObjectKeys,
                    exceptionSubReasons
                };
                
                onConfirm(submitData);
                onClose();
            })
            .catch(info => {
                console.log('Validate Failed:', info);
            });
    };

    // 处理异常原因变更，清空照片、二级原因并更新图片上传组件显示状态
    const handleExceptionReasonChange = (value: string) => {
        const selectedException = exceptionSelectData.find(
            exception => exception.exceptionReasonCode === value
        );
        
        setShowImageUploader(selectedException?.isUploadsImages !== false);
        
        form.setFieldsValue({
            photos: [],
            exceptionSubReasonCodes: []
        });
    };

    let warehouseListData = warehouseData.filter(warehouse => warehouse.siteType == SiteType.WAREHOUSE)

    const handleHubChange = (value: string) => {
        // 获取该hubId对应的warehouse
        const filteredWarehouses = warehouseListData?.filter(warehouse => warehouse.parentSiteId === value) || [];
        setWarehouseListForHubData(filteredWarehouses);
        // 清空仓库选择，因为 hub 改变了
        form.setFieldsValue({
            exceptionWarehouseId: undefined
        });
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-indigo-600 mr-2"></div>
                    <span>{t('components.modal.markAsException')}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={loading}>
                    {t('components.button.cancel')}
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
                    {t('components.button.ok')}
                </Button>,
            ]}
            width={500}
            maskClosable={!loading}
            closable={!loading}
        >
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                disabled={loading}
            >
                <Form.Item name="exceptionReasonCode" label={t('components.modal.reason')} rules={[{ required: true, message: t('components.modal.selectExceptionReason') }]}>
                    <Select
                        placeholder={t('components.modal.selectReason')}
                        allowClear
                        onChange={handleExceptionReasonChange}>
                        {exceptionSelectData.map(exception => (
                            <Select.Option 
                                key={exception.exceptionReasonCode} 
                                value={exception.exceptionReasonCode}
                            >
                                {getExceptionReasonText(exception)}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                {isAddressException && (
                    <Form.Item
                        label={t('components.modal.exceptionSubReason')}
                        name="exceptionSubReasonCodes"
                        rules={[{
                            required: true,
                            message: t('components.modal.pleaseSelectExceptionSubReason') || '',
                            ...(isMultiSelectSubReason ? { type: 'array' as const, min: 1 } : {})
                        }]}
                    >
                        <Select
                            mode={isMultiSelectSubReason ? "multiple" : undefined}
                            style={{ width: '100%' }}
                            placeholder={t('components.modal.selectExceptionSubReason') || ''}
                            allowClear
                            maxTagCount="responsive"
                        >
                            {addressSubReasons.map((sub) => (
                                <Select.Option key={sub.code} value={sub.code}>
                                    {selectedException ? getExceptionSubReasonTextByCode(selectedException, sub.code) : sub.code}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                {displayMode === DisplayMode.SHOW_HUB_WAREHOUSE && (
                <><Form.Item name="exceptionHubId" label={t('components.modal.hub')} rules={[{ required: true, message: t('components.modal.selectHubForException') }]}>
                        <Select
                            placeholder={t('components.modal.selectHub')}
                            allowClear
                            onChange={handleHubChange}
                        >
                            {warehouseData.map(warehouse => warehouse.siteType == SiteType.HUB ? (
                                <Select.Option
                                    key={warehouse.id}
                                    value={warehouse.id}
                                >
                                    {warehouse.name}
                                </Select.Option>
                            ) : null)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="exceptionWarehouseId" label={t('components.modal.warehouse')} rules={[{ required: true, message: t('components.modal.selectWarehouseForException') }]}>
                            <Select
                                placeholder={t('components.modal.selectWarehouse')}
                                allowClear
                            >
                            {/* 只有当 hub 被选择时才显示 N/A 选项和仓库列表 */}
                            {form.getFieldValue('exceptionHubId') && (
                                <>
                                    {/* 仓库列表 */}
                                    {warehouseListForHubData.map(warehouse => (
                                        <Select.Option
                                            key={warehouse.id}
                                            value={warehouse.id}
                                        >
                                            {warehouse.name}
                                        </Select.Option>
                                    ))}
                                </>
                            )}
                            </Select>
                        </Form.Item></>
                )}
                {showImageUploader && (
                    <Form.Item
                        label={t('components.modal.photo')}
                        name="photos"
                        rules={[{ required: true, message: 'Photo required' }]}>
                        <ImageUploader
                            value={parcel?.exceptionImageS3KeysJson || []}
                            tag={parcel?.trackingNumber}
                            displayUrls={parcel?.exceptionImageS3UrlsJson}
                            onChange={(urls: string[]) => form.setFieldsValue({photos: urls})}
                            maxCount={2}

                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default MarkAsExceptionModal;